/* eslint-disable react/no-danger, max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import isEmpty from 'lodash.isempty';

import { AppContext } from 'context/AppContext';

import Header from 'components/Header';
import Footer from 'components/Footer';
import WizardDialog from 'components/WizardDialog';
import CookieBar from 'components/CookieBar';

import { Main } from './Layout.styles';

const getMenuData = (menus, currentUrl, isDefaultSite) => {
  const showSplitMenu = isDefaultSite;

  let menuItems = [];
  let splitMenuItems = [];

  if (!isDefaultSite && menus.length > 0) {
    menuItems = menus[0].menuItems;
  } else if (isDefaultSite) {
    splitMenuItems = menus.map((item) => {
      const {
        menuType: typeName,
        rootPage: { id, url, title },
      } = item;
      return {
        id,
        url,
        title,
        typeName,
      };
    });
    const selectedMenuItems = menus.find((item) => {
      const {
        rootPage: { url },
      } = item;
      return currentUrl.startsWith(url);
    });

    if (!isEmpty(selectedMenuItems)) {
      menuItems = selectedMenuItems.menuItems;
    } else if (menus.length > 0) {
      menuItems = menus[0].menuItems;
    }
  }

  return {
    showSplitMenu,
    menuItems,
    splitMenuItems,
  };
};
export default function Layout({ children, gtmId, ...remaining }) {
  const { updateAppState } = useContext(AppContext);

  const {
    page: { seoTitle = '', seoDescription = '', url: pageUrl, metaTitle, metaDescription, metaImage, slug, logo },
    footer: footerData,
    menu: menus,
    siteName,
    isDefaultSite,
    isSplitPage,
    branding,
    urgentarticle,
    baseUrl,
    pageMeta,
    noPageData,
    cookiebar,
    sitesettings: { showWizard },
  } = remaining;
  const { statusCode } = remaining;
  const siteLogo = logo || branding.siteLogo;
  const { showSplitMenu, menuItems, splitMenuItems } = getMenuData(menus, pageUrl, isDefaultSite);

  const toggleModal = () => {
    document.body.classList.toggle('modal-open');
    updateAppState((state) => {
      return {
        ...state,
        openModal: !state.openModal,
        showCookieModal: !state.showCookieModal,
      };
    });
  };

  return (
    <>
      <Head>
        {seoTitle && <title>{seoTitle}</title>}
        <meta name="title" content={metaTitle} />
        {seoDescription && <meta name="description" content={seoDescription} />}

        {/* Favicon */}
        <link rel="shortcut icon" href="/assets/favicon.png" type="image/x-icon" />
        <link rel="icon" href="/assets/favicon.png" type="image/x-icon" />

        {/* Twitter */}
        <meta name="twitter:url" content={`${baseUrl}${slug}`} key="twurl" />
        <meta name="twitter:card" content="summary" key="twcard" />
        <meta name="twitter:site" content="@sportfondsennl" key="twsite" />
        <meta name="twitter:title" content={metaTitle} key="twtitle" />
        <meta name="twitter:description" content={metaDescription} key="twdesc" />

        {/* Open Graph */}
        <meta property="og:url" content={`${baseUrl}${slug}`} key="ogurl" />
        <meta property="og:locale" content="nl_NL" key="oglocale" />
        <meta property="og:type" content="website" key="ogtype" />
        <meta property="og:title" content={metaTitle} key="ogtitle" />
        <meta property="og:site_name" content={siteName} key="ogsitename" />
        <meta property="og:description" content={metaDescription} key="ogdesc" />
        {metaImage && <meta property="og:image" content={metaImage.url} key="ogimage" />}

        <link rel="canonical" href={baseUrl} />
      </Head>
      {/* Google Tag Manager (noscript) */}
      {gtmId && (
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          }}
        />
      )}
      {/* End Google Tag Manager (noscript) */}
      {/* If we have branding data we can render the header component */}
      {!isEmpty(siteLogo) && (
        <Header
          siteName={siteName}
          siteLogo={siteLogo}
          showNav={!isSplitPage}
          showSplitMenu={!isSplitPage && showSplitMenu}
          menuItems={menuItems}
          splitMenuItems={splitMenuItems}
          urgentArticle={urgentarticle.urgentArticle}
          preview={pageMeta}
        />
      )}
      {/* Top padding on the main element is here to offset the fixed main menu */}
      <Main
        className={`${statusCode || noPageData ? 'error-not-found' : ''}`}
        showSplitMenu={!isSplitPage && showSplitMenu}
        urgentArticle={urgentarticle.urgentArticle}
        inPreview={pageMeta && pageMeta.token}
      >
        {children}
      </Main>
      {showWizard && <WizardDialog />}
      {!isEmpty(siteLogo) && !isEmpty(footerData) && !isSplitPage && (
        <Footer siteName={siteName} siteLogo={siteLogo} {...footerData} />
      )}
      {cookiebar && <CookieBar {...cookiebar} toggleModal={toggleModal} />}
    </>
  );
}

Layout.defaultProps = {
  statusCode: null,
  preview: {
    title: '',
    description: '',
  },
  children: null,
  page: {
    seoTitle: '',
    seoDescription: '',
    url: '',
    metaTitle: '',
    metaDescription: '',
    metaImage: {
      url: '',
    },
    slug: '',
  },
  footer: {},
  menu: [],
  isSplitPage: false,
  isDefaultSite: false,
  urgentarticle: {
    urgentArticle: null,
  },
  siteName: '',
  baseUrl: '',
  branding: {
    siteLogo: {
      title: 'Sportfondsen',
      sm: {
        url: '/assets/logo_sportfondsen.png',
      },
      md: {
        url: '/assets/logo_sportfondsen.png',
      },
    },
  },
  noPageData: false,
  cookiebar: null,
  sitesettings: {
    showWizard: true,
  },
};

Layout.propTypes = {
  preview: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  children: PropTypes.node,
  page: PropTypes.shape({
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    url: PropTypes.string,
    metaTitle: PropTypes.string,
    metaDescription: PropTypes.string,
    metaImage: PropTypes.shape({
      url: PropTypes.string,
    }),
    slug: PropTypes.string,
    logo: PropTypes.shape({}),
  }),
  footer: PropTypes.shape({}),
  menu: PropTypes.arrayOf(PropTypes.shape({})),
  branding: PropTypes.shape({}),
  isDefaultSite: PropTypes.bool,
  siteName: PropTypes.string,
  statusCode: PropTypes.number,
  isSplitPage: PropTypes.bool,
  urgentarticle: PropTypes.shape({
    urgentArticle: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  baseUrl: PropTypes.string,
  noPageData: PropTypes.bool,
  cookiebar: PropTypes.shape({}),
  sitesettings: PropTypes.shape({
    showWizard: PropTypes.bool,
  }),
  gtmId: PropTypes.string.isRequired,
};
